<template>
  <form
    class="application-form"
    @submit.prevent="onSubmit"
    data-cy="application-form"
  >
    <UITabContainer
      v-if="tabs.length"
      class="schedule-tab-container"
      :tabs="tabs"
      :defaultTabId="activeTab"
      :hideTabHeader="ukdMode"
      @tabChanged="onTabChanged"
    >
      <template v-slot:addNewIngredient>
        <div class="tab-content" data-cy="new-ingredient-tab-content">
          <span class="tab-content__label" data-cy="tab-content-label"
            >Please enter information as it appears on the Kosher Certificate
            (LOC), if available.</span
          >
          <div class="form-control">
            <label
              class="form-label"
              for="ingredient-name"
              data-cy="ingredient-name-label"
              >Name *</label
            >
            <InputField
              class="form-input-field"
              type="text"
              inputId="ingredient-name"
              v-model="formData.ingredientName"
              :maxlength="225"
              required
              :disabled="editingDisabled"
              data-cy="ingredient-name-input"
            />
          </div>
          <div class="form-control">
            <label
              class="form-label"
              for="manufacturer-source"
              data-cy="manufacturer-source-label"
              >Manufacturer Source *</label
            >
            <ComboBox
              id="manufacturer-source"
              class="manufacturer-source"
              :data-items="pagedSources"
              text-field="Name"
              data-item-key="ID"
              :value="formData.selectedSource"
              :popup-settings="{ className: 'modal-popup', height: '250px' }"
              :allow-custom="true"
              :virtual="sourcesVirtual"
              :filterable="true"
              :maxlength="120"
              @pagechange="onSourcesPageChange($event)"
              @filterchange="onSourcesFilterChange"
              @change="onSourcesChange($event)"
              required
              :valid="formData.selectedSource != null"
              :disabled="editingDisabled"
              data-cy="manufacturer-source-dropdown"
            />
          </div>
          <div class="form-control">
            <label
              class="form-label"
              for="ingredient-brand"
              data-cy="ingredient-brand-label"
              >Brand</label
            >
            <InputField
              class="form-input-field"
              type="text"
              inputId="ingredient-brand"
              v-model="formData.brandName"
              :maxlength="100"
              :disabled="editingDisabled"
              data-cy="ingredient-brand-input"
            />
          </div>
        </div>
      </template>
      <template v-slot:addUkdId>
        <div class="tab-content" data-cy="by-ukd-id-tab-content">
          <span class="tab-content__label" data-cy="tab-content-label"
            >Enter a valid UKD ID and request will be expedited.</span
          >
          <div class="form-control">
            <label class="form-label" for="ukd-id" data-cy="ukd-id-label"
              >UKD ID *</label
            >
            <InputField
              class="form-input-field"
              type="text"
              inputId="ukd-id"
              v-model="formData.ukdId"
              :maxlength="50"
              :disabled="ukdIdEditingDisabled"
              :required="!ukdIdEditingDisabled"
              data-cy="ukd-id-input"
            />
          </div>
        </div>
      </template>
    </UITabContainer>
    <div class="form-control">
      <label
        class="form-label"
        for="ingredient-rmc"
        data-cy="ingredient-rmc-label"
        >RMC</label
      >
      <InputField
        class="form-input-field"
        type="text"
        inputId="ingredient-rmc"
        v-model="formData.rmc"
        :maxlength="100"
        data-cy="ingredient-rmc-input"
      />
    </div>
    <div class="documentation-container">
      <header class="form-header">
        <span data-cy="documentation-header-title"
          >Upload Supporting Documentation</span
        >
      </header>
      <IngredientAttachmentsView
        v-if="ingredient?.ID"
        :ingredientCartId="ingredient.ID"
      />
      <span class="form-control-title" data-cy="upload-label"
        >Upload Kosher Certificate, if available</span
      >
      <FileUpload v-model="formData.uploadedDocs" />
    </div>
    <header class="form-header" data-cy="plants-label">Plants</header>
    <IngredientPlantsGrid
      :plants="plantsWithSelection"
      :selectionDisabled="ingredient != null && ingredient != undefined"
    />
    <p
      v-show="!anyPlantSelected"
      class="form-plant-error"
      data-cy="plant-error"
    >
      Please select at least one plant.
    </p>
    <div class="submit-container">
      <Button
        v-if="showBackAction"
        type="button"
        theme-color="secondary"
        :disabled="formSubmitting"
        @click="closeForm(false, -1)"
        data-cy="back-button"
      >
        Return to Cart
      </Button>
      <LoadingButton
        type="submit"
        theme-color="primary"
        :disabled="!anyPlantSelected"
        :loading="formSubmitting"
        data-cy="submit-button"
        >{{
          ingredient ? "Update Cart" : "Add Ingredient To Cart"
        }}</LoadingButton
      >
    </div>
  </form>
</template>

<script setup lang="ts">
import type { IdAndName } from "@/Domain/IdAndName";
import IngredientPlantsGrid from "./IngredientPlantsGrid.vue";
import type { PlantWithSelection } from "@/api/scheduleB";
import { computed, onBeforeMount, ref } from "vue";
import { Button } from "@progress/kendo-vue-buttons";
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { SubApplicationType } from "@/Domain/SubApplicationType";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import type {
  ComboBoxChangeEvent,
  ComboBoxFilterChangeEvent,
  ComboBoxPageChangeEvent,
} from "@progress/kendo-vue-dropdowns";
import type { UploadFileInfo } from "@progress/kendo-vue-upload";
import { useScheduleAStore } from "@/stores/useScheduleAStore";
import type { IngredientDisplayInCart } from "@/Domain/IngredientDisplayInCart";
import IngredientAttachmentsView from "./IngredientAttachmentsView.vue";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";
import UITabContainer from "@/shared/ui/tabs/tab-container.vue";
import { ComboBox } from "@progress/kendo-vue-dropdowns";
import { filterBy } from "@progress/kendo-data-query";
import {
  getAttachmentFilesAsync,
  validateTotalUploadSize,
} from "@/shared/helpers/fileHelper";
import InputField from "@/shared/ui/inputs/InputField.vue";
import FileUpload from "@/shared/ui/inputs/FileUpload.vue";

const main = useMainStore();
const scheduleStore = useScheduleAStore();
const notificationStore = useNotificationStore();

interface FormData {
  ingredientName: string;
  selectedSource: IdAndName | null;
  brandName: string;
  ukdId: string;
  rmc: string;
  uploadedDocs: UploadFileInfo[];
}

const props = defineProps<{
  plants: IdAndName[];
  ingredient?: IngredientDisplayInCart;
  ownsIds?: number[];
  showBackAction?: boolean;
  ukdMode?: boolean;
}>();

const emit = defineEmits<{
  (e: "closeForm", submitted: boolean): void;
  (e: "submittingChanged", value: boolean): void;
}>();

const sources = ref<IdAndName[]>([]);
const pagedSources = ref<IdAndName[]>([]);
const sourcesSkip = ref(0);
const sourcesTotal = ref(0);
const pageSize = 50;

const sourcesVirtual = computed(() => {
  return {
    total: sourcesTotal.value,
    pageSize: pageSize,
    skip: sourcesSkip.value,
  };
});

const addNewIngredientTabId = "addNewIngredient";

const tabs = [
  { id: "addUkdId", label: " Add By UKD ID" },
  { id: addNewIngredientTabId, label: "Add New Ingredient" },
];

const activeTab = ref(tabs[0].id);

const formData = ref<FormData>({
  ingredientName: "",
  selectedSource: null,
  brandName: "",
  ukdId: "",
  rmc: "",
  uploadedDocs: [],
});

const formSubmitting = ref(false);

const componentName = "IngredientApplicationForm.vue";

const plantsWithSelection = computed(() => {
  return props.plants as PlantWithSelection[];
});

const anyPlantSelected = computed(() => {
  return plantsWithSelection.value.some((p) => p.selected);
});

const editingDisabled = computed(() => {
  if (!props.ingredient) return false;
  const type = props.ingredient.Type;
  // Always allow changes to RMC and attachments.
  // But some cart ingredients are copies of existing ingredients
  // on the Schedule A or UKD -- and label info cannot be modified!

  // Editing is disabled for ingredients with type INGREDIENT_IN_CART_TYPE_UKD
  // INGREDIENT_IN_CART_TYPE_SCHEDULEA and INGREDIENT_IN_CART_TYPE_ENTRY_FORM
  return type == 1 || type == 2 || (type == 3 && props.ingredient.LabelId > 0);
});

const ukdIdEditingDisabled = computed(() => {
  if (!props.ingredient) return false;
  const type = props.ingredient.Type;
  // Not allowing the ukdId to be edited on ukd*/Type3* ingredients in cart
  return editingDisabled.value || type == 3;
});

const updateIngredientSourcesAsync = async function () {
  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    startsWith: "",
  };

  await handleResponsePromiseAsync(
    api.get("IngredientSources", requestParams),
    (data: IdAndName[]) => {
      sources.value = data;
      sourcesTotal.value = data.length;
    },
    componentName,
    "updateIngredientSourcesAsync"
  );
};

const onSourcesFilterChange = function (...args: any[]) {
  const event = args[0] as ComboBoxFilterChangeEvent;
  // Replace "contains" operator with "startswith"
  // same logic as on the old page
  event.filter.operator = "startswith";

  const filteredData = filterBy(sources.value.slice(), event.filter);
  const data = filteredData.slice(0, pageSize);

  pagedSources.value = data;
  sourcesSkip.value = 0;
  sourcesTotal.value = filteredData.length;
};

const onSourcesPageChange = function (event: ComboBoxPageChangeEvent) {
  const skip = event.page.skip;
  const take = event.page.take;
  const newSubsetData = sources.value.slice(skip, skip + take);

  pagedSources.value = newSubsetData;
  sourcesSkip.value = skip;
};

const onSourcesChange = function (event: ComboBoxChangeEvent) {
  let value = event.target.value;

  // If user typed in a new source, value won't have an ID
  // Else, user selected an existing source
  if (value && value.ID === undefined) {
    value = {
      ...value,
      id: new Date().getTime(),
    };
  }
  formData.value.selectedSource = value;
  console.debug(`${componentName}, onSourcesChange()`, value);
};

const onTabChanged = function (tabId: string) {
  activeTab.value = tabId;
  console.debug(`${componentName}, onTabChanged()`, tabId);
};

const showErrorNotification = function (message: string) {
  notificationStore.notify({
    type: NotificationTypeEnum.error,
    message: message,
    duration: 5000,
  });
};

const fillFormData = function () {
  // If props.ingredient is null this is a new ingredient,
  // otherwise it is an existing ingredient we are modifying
  if (!props.ingredient) return;

  formData.value.ingredientName = props.ingredient.LabelName;
  formData.value.brandName = props.ingredient.BrandName;
  formData.value.ukdId = props.ingredient.AgencyId;
  formData.value.rmc = props.ingredient.Rmc;

  // Switch to the "Add New Ingredient" tab if ukd id is empty
  if (!formData.value.ukdId) {
    activeTab.value = addNewIngredientTabId;
  }
};

const fillFormSourceData = function () {
  // If props.ingredient is null this is a new ingredient,
  // otherwise it is an existing ingredient we are modifying
  if (!props.ingredient) return;

  const source = sources.value.find(
    (s) => s.ID == props.ingredient!.DistributorCompanyId
  );
  formData.value.selectedSource =
    source ??
    ({
      Name: props.ingredient.DistributorCompanyName,
      ID: props.ingredient.DistributorCompanyId,
    } as IdAndName);
};

const setFormSubmitting = function (value: boolean) {
  formSubmitting.value = value;
  emit("submittingChanged", value);
};

const onSubmit = async function () {
  try {
    if (formSubmitting.value) return;

    setFormSubmitting(true);

    // Validate total files size
    if (!validateTotalUploadSize(formData.value.uploadedDocs)) {
      setFormSubmitting(false);
      return;
    }

    const files = await getAttachmentFilesAsync(formData.value.uploadedDocs);

    const selectedPlants = plantsWithSelection.value
      .filter((p) => p.selected)
      .map((p) => p.ID.toString());

    let cartId = "";
    if (props.ingredient) cartId = props.ingredient.ID.toString();

    let ukdId = "";
    let ingredientName = "";
    let sourceId = 0;
    let sourceName = "";
    let brand = "";

    if (activeTab.value == "addNewIngredient" || props.ingredient) {
      ingredientName = formData.value.ingredientName;
      const existingSource = sources.value.find(
        (s) => s.ID == formData.value.selectedSource?.ID
      );
      sourceId = existingSource?.ID ?? 0;
      sourceName = formData.value.selectedSource?.Name ?? "";
      brand = formData.value.brandName;
    }

    // #18512 fix: when user modifies the cart (without modifying manufacturer),
    // it creates another manufacturer record
    if (
      props.ingredient &&
      sourceName == props.ingredient.DistributorCompanyName
    ) {
      sourceId = props.ingredient.DistributorCompanyId;
    }

    if (activeTab.value == "addUkdId" || props.ingredient) {
      ukdId = formData.value.ukdId;
    }

    await handleResponsePromiseAsync(
      api.post("VueSaveIngredientToCart", {
        UserLogin: main.userLogin,
        CompanyLogin: main.companyLogin,
        Id: cartId,
        UkdId: ukdId,
        Name: ingredientName,
        SourceId: sourceId,
        SourceName: sourceName,
        Brand: brand,
        Rmc: formData.value.rmc,
        OwnsIds: selectedPlants,
        Files: files,
      }),
      () => {
        closeForm(true, selectedPlants.length);
      },
      componentName,
      "onSubmit",
      (err, errResponse) => {
        const errMessage = errResponse?.message ?? err;
        showErrorNotification(errMessage);
      }
    );
  } catch (e) {
    console.error(`${componentName}, onSubmit()`, e);
    showErrorNotification("Error while submitting the form. Please try again");
  }
  setFormSubmitting(false);
};

const closeForm = function (submitted: boolean, cartCount: number = -1) {
  if (cartCount > 0) {
    notificationStore.notify({
      type: NotificationTypeEnum.success,
      message: `Ingredient added to the cart successfully`,
      duration: 5000,
    });
    scheduleStore.addToCartCount(SubApplicationType.Add, cartCount);
  }
  setFormSubmitting(false);
  emit("closeForm", submitted);
};

onBeforeMount(async () => {
  // Reset plant selection
  plantsWithSelection.value.forEach((p) => {
    p.selected = props.ownsIds?.includes(p.ID) || false;
  });

  fillFormData();
  await updateIngredientSourcesAsync();
  fillFormSourceData();
});
</script>

<style lang="scss">
.application-form {
  .tab-header {
    border-bottom: unset;
  }

  .tab-content {
    border: 1px solid #ddd;
    padding: 20px;
    border-radius: 0 4px 4px 4px;
    margin-bottom: 20px;

    &__label {
      display: block;
      margin-bottom: 20px;
      color: #585757;
    }

    .manufacturer-source {
      width: 70%;

      input {
        border: unset;
      }
    }
  }

  .form-header {
    background: #efefef;
    padding: 15px;
    font-size: 16px;
    margin-bottom: 20px;
    font-weight: bold;
    border-radius: 8px;
  }

  .form-row {
    align-items: baseline;
  }

  .form-control-title {
    display: block;
    font-weight: bold;
    margin-bottom: 12px;
  }

  .form-control {
    display: flex;
    align-items: center;
    margin-bottom: 20px;

    .form-label {
      display: block;
      margin-bottom: 5px;
      width: 25%;
    }

    .form-input-field {
      width: 70%;
    }
  }

  .upload-container {
    margin-bottom: 20px;
  }

  .form-plant-error {
    font-size: 12px;
    font-weight: bold;
    margin-bottom: 20px;
  }

  .plant-grid {
    max-height: 360px;
    margin-bottom: 20px;
  }

  .documentation-container {
    margin-bottom: 20px;
  }

  .submit-container {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
  }
}
</style>
